import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/api';
import { PageResponse, User, Structure, UserRequest } from '../types';
import Pagination from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 15;

// ── Helpers visuels ────────────────────────────────────────────────────────────

const getRoleConfig = (role: string) => {
  switch (role) {
    case 'SUPER_ADMIN':
      return {
        label: 'Super Admin',
        bg: 'bg-red-100',
        text: 'text-red-700',
        ring: 'ring-red-200',
        avatarBg: 'bg-red-500',
      };
    case 'RESPONSABLE':
      return {
        label: 'Responsable',
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        ring: 'ring-blue-200',
        avatarBg: 'bg-blue-500',
      };
    default:
      return {
        label: 'Utilisateur',
        bg: 'bg-green-100',
        text: 'text-green-700',
        ring: 'ring-green-200',
        avatarBg: 'bg-primary-500',
      };
  }
};

const UserAvatar: React.FC<{ user: User }> = ({ user }) => {
  const cfg = getRoleConfig(user.role);
  const initials = `${user.prenom.charAt(0)}${user.nom.charAt(0)}`.toUpperCase();
  return (
    <div
      className={`h-9 w-9 rounded-full ${cfg.avatarBg} flex items-center justify-center flex-shrink-0`}
    >
      <span className="text-xs font-bold text-white">{initials}</span>
    </div>
  );
};

const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const cfg = getRoleConfig(role);
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text} ring-1 ${cfg.ring}`}
    >
      {cfg.label}
    </span>
  );
};

// ── Composant principal ────────────────────────────────────────────────────────

const Utilisateurs: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const isAdmin = isSuperAdmin();

  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);

  // ── État SUPER_ADMIN : liste paginée serveur ──────────────────────────────
  const [result, setResult] = useState<PageResponse<User>>({
    content: [], page: 0, size: PAGE_SIZE, totalElements: 0, totalPages: 0, first: true, last: true,
  });

  // ── État RESPONSABLE : liste complète côté client ─────────────────────────
  const [allMyUsers, setAllMyUsers] = useState<User[]>([]);

  // ── État commun ───────────────────────────────────────────────────────────
  const [structures, setStructures] = useState<Structure[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form, setForm] = useState<UserRequest>({
    nom: '', prenom: '', email: '', password: '', role: 'UTILISATEUR', structureId: '',
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [error, setError] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [shouldGeneratePassword, setShouldGeneratePassword] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Pagination côté client pour RESPONSABLE ───────────────────────────────
  const filteredMyUsers = useMemo(() => {
    if (!search.trim()) return allMyUsers;
    const q = search.toLowerCase();
    return allMyUsers.filter(
      (u) =>
        u.nom.toLowerCase().includes(q) ||
        u.prenom.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [allMyUsers, search]);

  const myTotalPages = Math.max(1, Math.ceil(filteredMyUsers.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, myTotalPages - 1);
  const pagedMyUsers = filteredMyUsers.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const fetchPendingUsers = useCallback(async () => {
    if (!isAdmin) return;
    setPendingLoading(true);
    try {
      setPendingUsers(await apiService.getPendingUsers());
    } catch { /* silent */ }
    finally { setPendingLoading(false); }
  }, [isAdmin]);

  const handleActivate = async (id: string) => {
    setPendingActionId(id);
    try {
      await apiService.activateUser(id);
      await fetchPendingUsers();
      await fetchAdminUsers(currentPage, search);
    } catch { /* silent */ }
    finally { setPendingActionId(null); }
  };

  const handleReject = async (id: string) => {
    setPendingActionId(id);
    try {
      await apiService.rejectUser(id);
      await fetchPendingUsers();
    } catch { /* silent */ }
    finally { setPendingActionId(null); }
  };

  // ── Chargement des données ────────────────────────────────────────────────
  useEffect(() => {
    if (isAdmin) {
      apiService.getStructures().then(setStructures).catch(console.error);
      fetchPendingUsers();
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [isAdmin, fetchPendingUsers]);

  const fetchAdminUsers = useCallback(async (page: number, searchTerm: string) => {
    setIsLoading(true);
    try {
      const data = await apiService.getUsers({ page, size: PAGE_SIZE, search: searchTerm || undefined });
      setResult(data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchMyUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      setAllMyUsers(await apiService.getMyStructureUsers());
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminUsers(currentPage, search);
    } else {
      fetchMyUsers();
    }
  }, [isAdmin, currentPage, search, fetchAdminUsers, fetchMyUsers]);

  // ── Recherche avec debounce ───────────────────────────────────────────────
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setIsDebouncing(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setCurrentPage(0);
      setIsDebouncing(false);
    }, 400);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setCurrentPage(0);
    setIsDebouncing(false);
  };

  // ── Refresh après mutation ────────────────────────────────────────────────
  const refreshUsers = async () => {
    if (isAdmin) await fetchAdminUsers(currentPage, search);
    else await fetchMyUsers();
  };

  // ── Copier mot de passe ───────────────────────────────────────────────────
  const handleCopy = () => {
    navigator.clipboard?.writeText(generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingUser) {
        const updateData: UserRequest = { ...form };
        if (!updateData.password) delete updateData.password;
        if (!updateData.structureId) delete updateData.structureId;
        updateData.generatePassword = shouldGeneratePassword;
        const updated = await apiService.updateUser(editingUser.id, updateData);
        setGeneratedPassword(updated.generatedPassword || '');
      } else {
        const createData: UserRequest = { ...form };
        delete createData.password;
        delete createData.generatePassword;
        if (!createData.structureId) delete createData.structureId;
        const created = await apiService.createUser(createData);
        setGeneratedPassword(created.generatedPassword || '');
      }
      await refreshUsers();
      setShowModal(false);
      setEditingUser(null);
      setForm({ nom: '', prenom: '', email: '', password: '', role: 'UTILISATEUR', structureId: '' });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Une erreur est survenue');
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await apiService.deleteUser(userToDelete.id);
      await refreshUsers();
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (e) {
      console.error(e);
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setForm({
      nom: user.nom, prenom: user.prenom, email: user.email,
      password: '', generatePassword: false, role: user.role, structureId: user.structureId || '',
    });
    setShouldGeneratePassword(false);
    setGeneratedPassword('');
    setError('');
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setGeneratedPassword('');
    setShouldGeneratePassword(false);
    setError('');
    setForm({ nom: '', prenom: '', email: '', password: '', generatePassword: false, role: 'UTILISATEUR', structureId: '' });
    setShowModal(true);
  };

  // ── Données à afficher ────────────────────────────────────────────────────
  const displayedUsers = isAdmin ? result.content : pagedMyUsers;
  const totalElements = isAdmin ? result.totalElements : filteredMyUsers.length;
  const totalPages = isAdmin ? result.totalPages : myTotalPages;
  const activePage = isAdmin ? result.page : safePage;

  const startItem = activePage * PAGE_SIZE + 1;
  const endItem = Math.min((activePage + 1) * PAGE_SIZE, totalElements);

  // ── Skeleton loader ───────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-7 w-48 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="h-10 w-40 bg-gray-200 rounded-lg animate-pulse" />
        </div>
        {/* Table skeleton */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-100">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <div className="h-9 w-9 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                  <div className="h-3 w-56 bg-gray-100 rounded animate-pulse" />
                </div>
                <div className="h-6 w-20 bg-gray-100 rounded-full animate-pulse" />
                <div className="h-6 w-24 bg-gray-100 rounded-full animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── En-tête ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isAdmin ? 'Utilisateurs' : 'Utilisateurs de ma structure'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-primary-500" />
              {totalElements} utilisateur{totalElements > 1 ? 's' : ''} au total
            </span>
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <PlusIcon className="h-4 w-4" />
          Nouvel utilisateur
        </button>
      </div>

      {/* ── Mot de passe généré ───────────────────────────────────────────── */}
      {generatedPassword && (
        <div className="relative overflow-hidden bg-gradient-to-r from-primary-50 to-green-50 border border-primary-200 rounded-2xl p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 h-9 w-9 bg-primary-100 rounded-xl flex items-center justify-center">
                <CheckIcon className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-primary-800">Mot de passe généré avec succès</p>
                <p className="mt-1 text-sm text-primary-700">
                  Transmettez ce mot de passe à l'utilisateur :
                  <span className="ml-2 font-mono font-bold text-primary-900 bg-white/60 px-2 py-0.5 rounded-lg border border-primary-200">
                    {generatedPassword}
                  </span>
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border transition-all duration-200 ${
                copied
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-primary-700 border-primary-300 hover:bg-primary-50'
              }`}
            >
              {copied ? (
                <><CheckIcon className="h-4 w-4" />Copié !</>
              ) : (
                <><ClipboardDocumentIcon className="h-4 w-4" />Copier</>
              )}
            </button>
          </div>
          {/* decorative bar */}
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-400 to-green-400" />
        </div>
      )}

      {/* ── Comptes en attente d'activation (SUPER_ADMIN) ─────────────────── */}
      {isAdmin && (pendingLoading || pendingUsers.length > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-amber-200">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <ClockIcon className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-amber-800">Comptes en attente d'activation</p>
                <p className="text-xs text-amber-600">{pendingUsers.length} demande{pendingUsers.length > 1 ? 's' : ''} à traiter</p>
              </div>
            </div>
          </div>

          {pendingLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="divide-y divide-amber-100">
              {pendingUsers.map((u) => (
                <div key={u.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-amber-100/50 transition-colors">
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-amber-700">
                      {`${u.prenom.charAt(0)}${u.nom.charAt(0)}`.toUpperCase()}
                    </span>
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{u.prenom} {u.nom}</p>
                    <p className="text-xs text-gray-500 truncate">{u.email}</p>
                  </div>
                  {/* Structure */}
                  {u.structureNom && (
                    <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white text-gray-700 border border-amber-200 flex-shrink-0 max-w-[160px] truncate">
                      {u.structureNom}
                    </span>
                  )}
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleActivate(u.id)}
                      disabled={pendingActionId === u.id}
                      title="Activer le compte"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      {pendingActionId === u.id ? (
                        <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                      )}
                      Activer
                    </button>
                    <button
                      onClick={() => handleReject(u.id)}
                      disabled={pendingActionId === u.id}
                      title="Rejeter la demande"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white hover:bg-red-50 text-red-600 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <XCircleIcon className="h-3.5 w-3.5" />
                      Rejeter
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Barre de recherche ────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="relative max-w-xl">
          <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher par nom, prénom ou email…"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="block w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-colors duration-150 placeholder:text-gray-400"
          />
          {/* Spinner debounce */}
          {isDebouncing && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="h-4 w-4 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
            </div>
          )}
          {/* Clear button */}
          {searchInput && !isDebouncing && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Effacer la recherche"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {search && (
          <p className="mt-2 text-xs text-gray-500">
            {totalElements} résultat{totalElements > 1 ? 's' : ''} pour « <span className="font-medium text-gray-700">{search}</span> »
          </p>
        )}
      </div>

      {/* ── Tableau ───────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

        {displayedUsers.length === 0 ? (
          /* État vide */
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="h-16 w-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
              <UsersIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-800 mb-1">
              {search ? 'Aucun résultat trouvé' : 'Aucun utilisateur enregistré'}
            </h3>
            <p className="text-sm text-gray-400 max-w-xs">
              {search
                ? `Aucun utilisateur ne correspond à « ${search} ». Essayez avec d'autres termes.`
                : 'Commencez par ajouter un premier utilisateur en cliquant sur le bouton ci-dessus.'}
            </p>
            {search && (
              <button
                onClick={clearSearch}
                className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Effacer la recherche
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  {isAdmin && (
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Structure
                    </th>
                  )}
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pr-6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {displayedUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-primary-50/30 transition-colors duration-100 group"
                  >
                    {/* Utilisateur : avatar + nom + email */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <UserAvatar user={user} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {user.prenom} {user.nom}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{user.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Rôle */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <RoleBadge role={user.role} />
                    </td>

                    {/* Structure (admin only) */}
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.structureNom ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 ring-1 ring-gray-200">
                            {user.structureNom}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300 italic">— non assigné</span>
                        )}
                      </td>
                    )}

                    {/* Statut */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-500 flex-shrink-0" />
                        Actif
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <button
                          onClick={() => openEditModal(user)}
                          title="Modifier"
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-150"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => { setUserToDelete(user); setShowDeleteModal(true); }}
                          title="Supprimer"
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer pagination inline */}
        {displayedUsers.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/40 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Affichage <span className="font-semibold text-gray-700">{startItem}–{endItem}</span> sur{' '}
              <span className="font-semibold text-gray-700">{totalElements}</span> utilisateur{totalElements > 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      <Pagination
        page={activePage}
        totalPages={totalPages}
        totalElements={totalElements}
        size={PAGE_SIZE}
        onPageChange={setCurrentPage}
      />

      {/* ══════════════════════════════════════════════════════════════════════
          Modal Créer / Modifier
      ══════════════════════════════════════════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">

            {/* Header vert */}
            <div className="bg-primary-600 px-6 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-white">
                  {editingUser ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
                </h3>
                <p className="text-xs text-primary-200 mt-0.5">
                  {editingUser
                    ? `${editingUser.prenom} ${editingUser.nom}`
                    : 'Remplissez les informations ci-dessous'}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 text-primary-200 hover:text-white hover:bg-primary-500 rounded-lg transition-colors"
                aria-label="Fermer"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Corps */}
            <div className="p-6">
              {error && (
                <div className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">

                {/* Prénom + Nom */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Prénom <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="ex : Awa"
                      value={form.prenom}
                      onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Nom <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="ex : Traoré"
                      value={form.nom}
                      onChange={(e) => setForm({ ...form, nom: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Adresse email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="ex : awa.traore@gov.bf"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>

                {/* Regénérer mot de passe (mode édition) */}
                {editingUser && (
                  <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50 cursor-pointer hover:bg-primary-50 hover:border-primary-200 transition-colors">
                    <input
                      type="checkbox"
                      checked={shouldGeneratePassword}
                      onChange={(e) => {
                        setShouldGeneratePassword(e.target.checked);
                        setForm({ ...form, password: '', generatePassword: e.target.checked });
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Générer un nouveau mot de passe</p>
                      <p className="text-xs text-gray-400">Un nouveau mot de passe sera créé automatiquement</p>
                    </div>
                  </label>
                )}

                {/* Rôle + Structure — SUPER_ADMIN uniquement */}
                {isAdmin && (
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                        Rôle <span className="text-red-400">*</span>
                      </label>
                      <select
                        required
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value as any })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      >
                        <option value="UTILISATEUR">Utilisateur</option>
                        <option value="RESPONSABLE">Responsable</option>
                        <option value="SUPER_ADMIN">Super Admin</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                        Structure
                      </label>
                      <select
                        value={form.structureId}
                        onChange={(e) => setForm({ ...form, structureId: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      >
                        <option value="">— Aucune —</option>
                        {structures.map((s) => (
                          <option key={s.id} value={s.id}>{s.sigle || s.nom}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    {editingUser ? 'Enregistrer' : 'Créer l\'utilisateur'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Modal Suppression
      ══════════════════════════════════════════════════════════════════════ */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">

            {/* Header rouge */}
            <div className="bg-red-600 px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 bg-red-500 rounded-xl flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base font-bold text-white">Supprimer l'utilisateur</h3>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1.5 text-red-200 hover:text-white hover:bg-red-500 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Corps */}
            <div className="p-6">
              <p className="text-sm text-gray-600 leading-relaxed">
                Vous êtes sur le point de supprimer le compte de{' '}
                <span className="font-semibold text-gray-900">
                  {userToDelete.prenom} {userToDelete.nom}
                </span>
                . Cette action est <span className="font-semibold text-red-600">irréversible</span> et
                supprimera définitivement toutes ses données.
              </p>

              <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                <div className="flex items-center gap-2">
                  <UserAvatar user={userToDelete} />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{userToDelete.prenom} {userToDelete.nom}</p>
                    <p className="text-xs text-gray-500">{userToDelete.email}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Oui, supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Utilisateurs;
